'use client';
import Head from 'next/head';
import { useState, useMemo } from 'react';
import { Pizza } from 'lucide-react';
import MethodPageLayout from '@/components/layouts/MethodPageLayout';
import FractionPizza from '@/components/methods/FractionPizza';
import StepperControl from '@/components/common/StepperControl';
import SliderControl from '@/components/common/SliderControl';
import ColorSwatchRow from '@/components/common/ColorSwatchRow';
import InfoCard from '@/components/common/InfoCard';

const PIZZA_COLORS = ['#534AB7', '#0F6E56', '#854F0B', '#185FA5', '#993C1D', '#f4a261'];

export default function PizzaVisualiserPage() {
    const [totalSlices, setTotalSlices] = useState(6);
    const [highlightedSlices, setHighlightedSlices] = useState(2);
    const [color, setColor] = useState('#0F6E56');

    const handleTotalChange = (val: number) => {
        setTotalSlices(val);
        if (highlightedSlices > val) {
            setHighlightedSlices(val);
        }
    };

    const onReset = () => {
        setTotalSlices(6);
        setHighlightedSlices(2);
        setColor('#0F6E56');
    };

    const gcd = (a: number, b: number): number => {
        return b === 0 ? a : gcd(b, a % b);
    };

    const infoRows = useMemo(() => {
        const divisor = gcd(highlightedSlices, totalSlices);
        const simplifiedNum = highlightedSlices / divisor;
        const simplifiedDen = totalSlices / divisor;
        const decimal = (highlightedSlices / totalSlices).toFixed(2);
        const percentage = Math.round((highlightedSlices / totalSlices) * 100);

        return [
            { key: 'Fraction', value: `${highlightedSlices} / ${totalSlices}` },
            { key: 'Simplified', value: `${simplifiedNum} / ${simplifiedDen}` },
            { key: 'Decimal', value: decimal },
            { key: 'Percentage', value: `${percentage}%` },
        ];
    }, [highlightedSlices, totalSlices]);

    const controls = (
        <>
            <StepperControl
                label="Total slices"
                value={totalSlices}
                min={1}
                max={50}
                onChange={handleTotalChange}
            />
            <SliderControl
                label="Highlighted slices"
                value={highlightedSlices}
                min={0}
                max={totalSlices}
                onChange={setHighlightedSlices}
            />
            <ColorSwatchRow
                label="Pizza Color"
                colors={PIZZA_COLORS}
                value={color}
                onChange={setColor}
            />
            <InfoCard rows={infoRows} />
        </>
    );

    return (
        <>
            <Head>
                <title>Pizza Slice Visualiser</title>
            </Head>
            <MethodPageLayout
                icon={<Pizza size={20} />}
                iconColor="teal"
                title="Pizza slice visualiser"
                description="Interactive pizza diagram to visualise fractions."
                category="visualiser"
                controls={controls}
                onReset={onReset}
            >
                <FractionPizza
                    numerator={highlightedSlices}
                    denominator={totalSlices}
                    color={color}
                />
            </MethodPageLayout>
        </>
    );
}
